import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ButtonV2, Flex, TextV2, useDisclosure, useToast } from '@withjoy/joykit';
import { CheckIcon } from '@assets/icons';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { PhotoGallery, WarningTriangle } from '@withjoy/joykit/icons';
import {
  ComponentType,
  MagicTokenPermissions,
  useAddMediaGalleryComponentMutation,
  useCreateMediaCollectionMutation,
  useGenerateMagicTokenForMediaCollectionMutation,
  useGetEventMediaLazyQuery,
  useMediaCollectionsQuery,
  useSetMediaTokenPermissionsMutation,
  useUpdateMediaGalleryComponentMutation,
  WebsiteEditorMediaGalleryComponentFragment,
} from '@graphql/generated';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { UpdateCollectionDialog } from '@apps/admin/routes/WebsiteOverview/routes/Photos/components';
import { UpdateCollectionFormFields } from '@apps/admin/routes/WebsiteOverview/routes/Photos/components/UpdateCollectionDialog/UpdateCollectionDialog.types';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useWebsiteEditorCustomPageTelemetry } from '@website-editor/features/custom-page/WebsiteEditorCustomPage.telemetry';
import { PhotoUploader } from '@apps/admin/routes/WebsiteOverview/routes/Photos/routes/ViewCollection/components/PhotoUploader/PhotoUploader';
import { CollectionLists, PrivacyAlertDialog, ZeroState } from '..';
import { CUSTOM_PAGE_ACTIONS } from '@website-editor/customComponents/CustomComponents.constants';
import { useWebsiteEditorPhotoGalleryTelemetry } from '../../PhotoGallery.telemetry';
import { LayoutVirtualScrollableArea } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutVirtualScrollableArea';
import { BottomActions } from '@website-editor/features/custom-page/components/BottomActions/BottomActions';
import { EventMediaPhoto, SelectPhotoGalleryProps } from '../../PhotoGallery.types';

export const SelectPhotoGallery: FC<SelectPhotoGalleryProps> = props => {
  const { containerId, isSingleComponent, photoGalleryData, pageSlug, isEditMode } = props;

  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [isMediaUploading, setIsMediaUploading] = useState(false);

  const { isOpen: isOpenCollectionDialog, onClose: handleCloseCollectionDialog, onOpen: handleOpenCollectionDialog } = useDisclosure();
  const { isOpen: isOpenPickerDialog, onOpen: onOpenPickerDialog, onClose: onClosePickerDialog } = useDisclosure();
  const { isOpen: isOpenPrivacyAlertDialog, onOpen: onOpenPrivacyAlertDialog, onClose: onClosePrivacyAlertDialog } = useDisclosure();

  const telemetry = useWebsiteEditorPhotoGalleryTelemetry();
  const customPageTelemetry = useWebsiteEditorCustomPageTelemetry();

  const { setCustomComponentBlockData } = useCustomComponentStateContext<WebsiteEditorMediaGalleryComponentFragment>();
  const { replaceAndNavigateToScreen } = useNavigationPaneContext();
  const { eventInfo, eventHandle = '' } = useEventInfo();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { toast } = useToast();

  const { tq } = useTranslation('websiteEditor');

  const { data: mediaCollectionsResponse, loading: loadingMediaCollections } = useMediaCollectionsQuery({
    variables: {
      eventHandle,
    },
    batchMode: 'fast',
  });
  const [getEventMedia, { data: eventMedia, loading: isLoadingEventMedia }] = useGetEventMediaLazyQuery();
  const [createMediaCollection, { loading: isCreatingMediaCollection }] = useCreateMediaCollectionMutation();
  const [addMediaGalleryComponent, { loading: isAddingMediaGalleryComponent }] = useAddMediaGalleryComponentMutation({
    onCompleted: result => {
      customPageTelemetry.websitePageComponentUpdated(pageSlug, 'Add', {
        action: 'added',
        id: result.addMediaGalleryComponent.id,
        componentType: ComponentType.mediaGallery,
      });
      toast(tq`Success! Photo Gallery has been saved.`, { icon: <CheckIcon /> });
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    },
    onError: () => {
      toast(tq`You have reached the block limit. To add more blocks please create a new page or replace an existing block.`, { icon: <WarningTriangle />, duration: 5000 });
    },
  });
  const [updateMediaGalleryComponent, { loading: updatingMediaGalleryComponent }] = useUpdateMediaGalleryComponentMutation({
    onCompleted: result => {
      customPageTelemetry.websitePageComponentUpdated(pageSlug, 'Update', {
        action: 'updated',
        id: result.updateMediaGalleryComponent.id,
        componentType: ComponentType.mediaGallery,
      });
      toast(tq`Success! Photo Gallery has been saved.`, { icon: <CheckIcon /> });
      setCustomComponentBlockData({} as WebsiteEditorMediaGalleryComponentFragment);
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    },
    onError: () => {
      toast(tq`An error occurred while saving your photo gallery. Please try again.`, { icon: <WarningTriangle /> });
    },
  });
  const [setMediaTokenPermission, { loading: isUpdatingMediaTokenPermission }] = useSetMediaTokenPermissionsMutation();
  const [generateMagicToken] = useGenerateMagicTokenForMediaCollectionMutation();

  const media = useMemo(() => {
    const mediaPhotos = (eventMedia?.eventByName?.media?.filter(m => m.__typename === 'MediaPhoto') || []) as EventMediaPhoto[];
    return mediaPhotos?.sort((a, b) => {
      return b.createdAt.milliseconds - a.createdAt.milliseconds;
    });
  }, [eventMedia]);

  useEffect(() => {
    if (isEditMode && photoGalleryData?.mediaCollection?.id) {
      setSelectedCollectionId(photoGalleryData.mediaCollection.id);
    }
  }, [isEditMode, photoGalleryData?.mediaCollection?.id]);

  const fetchEventMedia = useEventCallback(() => {
    getEventMedia({
      variables: {
        eventHandle,
      },
    });
  });

  const updatePhotoUploadStatus = useEventCallback((status: boolean) => {
    setIsMediaUploading(status);
  });

  const handleCreateAlbum = useEventCallback(() => {
    handleOpenCollectionDialog();
  });

  const handleCreateMediaCollection = useCallback(
    (values: UpdateCollectionFormFields) => {
      createMediaCollection({
        variables: {
          payload: {
            eventId: eventInfo?.eventId || '',
            name: values.name,
          },
        },
        refetchQueries: ['MediaCollections'],
        awaitRefetchQueries: true,
      }).then(response => {
        if (response.data) {
          telemetry.createNewAlbumSuccess(eventInfo?.eventId || '', values.name);
          handleCloseCollectionDialog();
          setSelectedCollectionId(response.data.createMediaCollection.id);
          onOpenPickerDialog();
          fetchEventMedia();
        }
      });
    },
    [createMediaCollection, eventInfo?.eventId, fetchEventMedia, handleCloseCollectionDialog, onOpenPickerDialog, telemetry],
  );

  const handleCancel = useEventCallback(() => {
    setCustomComponentBlockData({} as WebsiteEditorMediaGalleryComponentFragment);
    replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
  });

  const handleSelectCollection = useEventCallback((collectionId: string) => {
    setSelectedCollectionId(collectionId);
  });

  const onSaveClick = useEventCallback(() => {
    if (selectedCollectionId) {
      const isCollectionPublic = mediaCollectionsResponse?.eventByName?.mediaCollections
        ?.find(collection => collection.id === selectedCollectionId)
        ?.magicTokenPermissions?.includes(MagicTokenPermissions.view);
      if (isCollectionPublic) {
        handleSave({ makeCollectionPublic: false });
      } else {
        onOpenPrivacyAlertDialog();
      }
    }
  });

  const handleSave = useEventCallback(({ makeCollectionPublic }: { makeCollectionPublic: boolean }) => {
    if (selectedCollectionId) {
      onClosePrivacyAlertDialog();
      if (makeCollectionPublic) {
        // Update the media collection to be public and generate a magic token
        setMediaTokenPermission({
          variables: {
            payload: {
              collectionId: selectedCollectionId,
              permissions: [MagicTokenPermissions.view],
            },
          },
          refetchQueries: ['MediaCollections'],
        });
        generateMagicToken({
          variables: {
            collectionId: selectedCollectionId,
          },
        });
      }
      if (isEditMode && photoGalleryData?.id) {
        updateMediaGalleryComponent({
          variables: {
            payload: {
              id: photoGalleryData.id,
              mediaCollectionId: selectedCollectionId,
              sortOrder: photoGalleryData.sortOrder,
            },
          },
          awaitRefetchQueries: true,
          refetchQueries: ['PageContainerBySlug'],
        }).then(() => {
          updateWebsitePreview();
        });
      } else {
        addMediaGalleryComponent({
          variables: {
            payload: {
              containerId,
              mediaCollectionId: selectedCollectionId,
              sortOrder: photoGalleryData?.sortOrder ?? 0,
            },
          },
          awaitRefetchQueries: true,
          refetchQueries: ['PageContainerBySlug'],
        }).then(() => {
          updateWebsitePreview();
        });
      }
    }
  });

  return (
    <>
      <LayoutVirtualScrollableArea>
        <Flex
          width="100%"
          paddingX={5}
          paddingTop={6}
          paddingBottom={pxToRem(126)}
        >
          <Flex
            width="100%"
            flexDirection="column"
            alignItems="center"
            paddingX={6}
            gap={6}
          >
            <Flex
              width="100%"
              height={pxToRem(82)}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap={6}
              borderRadius={3}
              border="1px dashed"
              borderColor="mono4"
              backgroundColor="mono1"
            >
              <ButtonV2
                size="lg"
                variant={loadingMediaCollections || mediaCollectionsResponse?.eventByName?.mediaCollections?.length === 0 ? 'solid' : 'outline'}
                shape="rounded"
                intent="neutral"
                startIcon={<PhotoGallery size={24} />}
                onClick={handleCreateAlbum}
              >
                {tq`Create Album`}
              </ButtonV2>
            </Flex>

            <Flex
              width="100%"
              flexDirection="column"
              alignItems="flex-start"
              gap={5}
            >
              <Flex
                width="100%"
                flexDirection="column"
                alignItems="flex-start"
                gap={1}
              >
                <TextV2
                  typographyVariant="button2"
                  color="mono14"
                >
                  {tq`Select Album`}
                </TextV2>
                {(loadingMediaCollections || mediaCollectionsResponse?.eventByName?.mediaCollections?.length === 0) && (
                  <TextV2
                    typographyVariant="label1"
                    color="mono14"
                  >
                    {tq`All your albums will appear here.`}
                  </TextV2>
                )}
              </Flex>

              {loadingMediaCollections || mediaCollectionsResponse?.eventByName?.mediaCollections?.length === 0 ? (
                <ZeroState />
              ) : (
                <CollectionLists
                  mediaCollections={mediaCollectionsResponse?.eventByName?.mediaCollections}
                  handleSelectCollection={handleSelectCollection}
                  selectedCollectionId={selectedCollectionId}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
        <PhotoUploader
          isOpenPickerDialog={isOpenPickerDialog}
          onOpenPickerDialog={onOpenPickerDialog}
          onClosePickerDialog={onClosePickerDialog}
          collectionId={selectedCollectionId || ''}
          containerId={containerId}
          eventId={eventInfo?.eventId || ''}
          isFetching={isLoadingEventMedia}
          media={media}
          showUploaderEntryPointCTA={false}
          updateUploadStatus={updatePhotoUploadStatus}
        />
      </LayoutVirtualScrollableArea>

      <UpdateCollectionDialog
        isOpenCollectionDialog={isOpenCollectionDialog}
        handleCloseCollectionDialog={handleCloseCollectionDialog}
        handleSave={handleCreateMediaCollection}
        isUpdating={isCreatingMediaCollection}
      />

      <PrivacyAlertDialog
        isOpen={isOpenPrivacyAlertDialog}
        onClose={onClosePrivacyAlertDialog}
        onCTAClick={handleSave}
        disabled={isUpdatingMediaTokenPermission}
      />

      <BottomActions
        onCancel={handleCancel}
        onSave={onSaveClick}
        isLoading={isAddingMediaGalleryComponent || updatingMediaGalleryComponent || isMediaUploading || isUpdatingMediaTokenPermission}
        isSingleComponent={isSingleComponent}
        isSaveDisabled={!selectedCollectionId || isMediaUploading}
      />
    </>
  );
};

SelectPhotoGallery.displayName = 'SelectPhotoGallery';
